import { useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import { formatDateRange } from '../lib/dateUtils';
import { useCategories } from '../hooks/useCategories';
import { t } from '../lib/i18n';
import CalendarWidget from '../components/CalendarWidget';
import './HomePage.css';

function isFuture(startDateStr) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(startDateStr + 'T00:00:00') > today;
}

function calcDaysUntil(startDateStr) {
  const start = new Date(startDateStr + 'T00:00:00');
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.floor((start - today) / 86400000);
}

// 시작일부터 오늘까지 몇 일째인지 (시작일 = D+1)
function calcDDay(startDateStr) {
  const start = new Date(startDateStr + 'T00:00:00');
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.floor((today - start) / 86400000) + 1;
}

function groupByYear(records, sortOrder = 'newest') {
  const asc = sortOrder === 'oldest';
  const map = new Map();
  for (const record of records) {
    const year = record.start_date?.slice(0, 4) ?? '?';
    if (!map.has(year)) map.set(year, []);
    map.get(year).push(record);
  }
  return Array.from(map.entries())
    .sort(([a], [b]) => asc ? a.localeCompare(b) : b.localeCompare(a))
    .map(([year, recs]) => ({
      year,
      records: recs.sort((a, b) => asc
        ? (a.start_date ?? '').localeCompare(b.start_date ?? '')
        : (b.start_date ?? '').localeCompare(a.start_date ?? '')),
    }));
}

function AdCard({ ad }) {
  if (!ad) {
    return (
      <div className="ad-card ad-card--placeholder">
        <span className="ad-label">광고 · AD</span>
        <span className="ad-icon">📢</span>
        <p className="ad-text">이 자리에 광고를 넣어보세요</p>
        <p className="ad-contact">문의: onetiger87@gmail.com</p>
      </div>
    );
  }
  return (
    <a className="ad-card ad-card--banner" href={ad.link_url} target="_blank" rel="noopener noreferrer">
      <span className="ad-label">광고 · AD{ad.title ? ` · ${ad.title}` : ''}</span>
      <img className="ad-banner-img" src={ad.image_url} alt={ad.title || '광고'} />
    </a>
  );
}

const LONG_PRESS_MS = 500;
const MOVE_TOLERANCE = 10;

function RecordCard({ record, onNavigate, onLongPress }) {
  const timerRef = useRef(null);
  const startRef = useRef(null);
  const firedRef = useRef(false);
  const [pressing, setPressing] = useState(false);

  const cancelPress = () => {
    clearTimeout(timerRef.current);
    timerRef.current = null;
    setPressing(false);
  };

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const handlePointerDown = (e) => {
    if (!onLongPress || (e.pointerType === 'mouse' && e.button !== 0)) return;
    firedRef.current = false;
    startRef.current = { x: e.clientX, y: e.clientY };
    setPressing(true);
    timerRef.current = setTimeout(() => {
      firedRef.current = true;
      setPressing(false);
      if (navigator.vibrate) navigator.vibrate(30);
      window.getSelection()?.removeAllRanges();
      onLongPress(record);
    }, LONG_PRESS_MS);
  };

  const handlePointerMove = (e) => {
    if (!timerRef.current || !startRef.current) return;
    const dx = Math.abs(e.clientX - startRef.current.x);
    const dy = Math.abs(e.clientY - startRef.current.y);
    if (dx > MOVE_TOLERANCE || dy > MOVE_TOLERANCE) cancelPress();
  };

  const handleClick = () => {
    // 롱프레스 직후 발생하는 click은 무시
    if (firedRef.current) {
      firedRef.current = false;
      return;
    }
    onNavigate('editRecord', record);
  };

  return (
    <div
      className={`record-card${pressing ? ' record-card--pressing' : ''}`}
      onClick={handleClick}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={cancelPress}
      onPointerLeave={cancelPress}
      onPointerCancel={cancelPress}
      onContextMenu={(e) => e.preventDefault()}
      role="button"
    >
      <div className="record-card-left">
        <span className="record-emoji">{record.category_emoji ?? '📌'}</span>
      </div>
      <div className="record-card-body">
        <div className="record-title-row">
          <p className="record-title">{record.title}</p>
          {isFuture(record.start_date) ? (
            <span className="record-badge-upcoming">예정 D-{calcDaysUntil(record.start_date)}</span>
          ) : !record.end_date && (
            <span className="record-dday">D+{calcDDay(record.start_date)}</span>
          )}
        </div>
        {record.category_name && (
          <span className="record-category-chip">{record.category_name}</span>
        )}
        <p className="record-meta">
          {formatDateRange(record.start_date, record.end_date, record.is_ongoing)}
          {record.location_name && (
            <span className="record-meta-location"> · 📍 {record.location_name}</span>
          )}
        </p>
        {record.memo && <p className="record-memo">{record.memo}</p>}
      </div>
      {record.photos?.length > 0 && (
        <div className="record-thumbnail">
          <img src={record.photos[0]} alt="썸네일" />
        </div>
      )}
    </div>
  );
}

export default function HomePage({ user, onNavigate, refreshSignal, currentProfile }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [sortOrder, setSortOrder] = useState(() => localStorage.getItem('lifelog_sort') || 'newest');
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarDate, setCalendarDate] = useState(null);
  const [adList, setAdList] = useState([null]); // null = 모집 플레이스홀더
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // 롱프레스 손가락을 떼는 순간의 click이 팝업 버튼에 먹히지 않도록, 팝업 안에서 새로 누른 뒤에만 반응
  const sheetArmedRef = useRef(false);
  const searchInputRef = useRef(null);
  const { categories: allCategories } = useCategories(user.id);

  const handleSortChange = (order) => {
    setSortOrder(order);
    localStorage.setItem('lifelog_sort', order);
    setShowSortMenu(false);
  };

  const fetchRecords = async () => {
    setLoading(true);
    setFetchError('');

    // inner join으로 1회 쿼리 (record_shares + records)
    const { data, error } = await supabase
      .from('records')
      .select('*, record_shares!inner(profile_id)')
      .eq('user_id', user.id)
      .eq('record_shares.profile_id', currentProfile.id)
      .order('start_date', { ascending: true })
      .order('created_at', { ascending: true });

    if (error) setFetchError(error.message);
    else setRecords(data || []);

    // 광고 조회 (실제 광고 + 모집 플레이스홀더를 섞어서 순환)
    const { data: ads } = await supabase
      .from('ads')
      .select('title, image_url, link_url')
      .eq('is_active', true);
    // null = 광고 모집 카드, 항상 포함
    const list = [...(ads || []), null];
    // 셔플
    for (let i = list.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [list[i], list[j]] = [list[j], list[i]];
    }
    setAdList(list);

    setLoading(false);
  };

  useEffect(() => {
    // 프로필이 로딩될 때까지 대기 (깜빡임 방지)
    if (!currentProfile?.id) return;
    fetchRecords();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id, refreshSignal, currentProfile?.id]);

  const openDeleteSheet = (record) => {
    sheetArmedRef.current = false;
    setDeleteTarget(record);
  };

  const closeDeleteSheet = () => {
    if (!sheetArmedRef.current || deleting) return;
    setDeleteTarget(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget || !sheetArmedRef.current) return;
    setDeleting(true);
    const { error } = await supabase.from('records').delete().eq('id', deleteTarget.id);
    setDeleting(false);
    if (error) {
      alert('삭제 실패: ' + error.message);
      return;
    }
    setRecords((prev) => prev.filter((r) => r.id !== deleteTarget.id));
    setDeleteTarget(null);
  };

  const openSearch = () => {
    setShowSearch(true);
    setTimeout(() => searchInputRef.current?.focus(), 50);
  };

  const closeSearch = () => {
    setShowSearch(false);
    setSearchQuery('');
  };

  // records에서 카테고리 추출 후 allCategories sort_order 순서로 정렬
  const recordCatMap = records.reduce((m, r) => {
    if (r.category_name && !m.has(r.category_name)) {
      m.set(r.category_name, r.category_emoji ?? '');
    }
    return m;
  }, new Map());
  const catOrderMap = new Map(allCategories.map((c, i) => [c.name, i]));
  const categoryOptions = [...recordCatMap.entries()]
    .map(([name, emoji]) => ({ name, emoji }))
    .sort((a, b) => {
      const oa = catOrderMap.has(a.name) ? catOrderMap.get(a.name) : 9999;
      const ob = catOrderMap.has(b.name) ? catOrderMap.get(b.name) : 9999;
      return oa - ob;
    });

  // 클라이언트 사이드 필터링
  const q = searchQuery.trim().toLowerCase();
  const calendarFiltered = calendarDate
    ? records.filter((r) => r.start_date === calendarDate)
    : records;
  const categoryFiltered = selectedCategory
    ? calendarFiltered.filter((r) => r.category_name === selectedCategory)
    : calendarFiltered;
  const filteredRecords = q
    ? categoryFiltered.filter((r) =>
        r.title?.toLowerCase().includes(q) ||
        r.category_name?.toLowerCase().includes(q) ||
        r.memo?.toLowerCase().includes(q) ||
        r.location_name?.toLowerCase().includes(q)
      ).sort((a, b) => (b.start_date ?? '').localeCompare(a.start_date ?? ''))
    : categoryFiltered;

  const yearGroups = q ? [] : groupByYear(filteredRecords, sortOrder);
  const isEmpty = !loading && !fetchError && filteredRecords.length === 0;

  return (
    <div className="home-page">
      <div className="home-sticky-top">
        <header className="home-header">
          <h1 className="home-title">인생로그</h1>
          <div className="home-header-actions">
            <button className="home-search-btn" onClick={openSearch} aria-label="검색">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </button>
            <button
              className={`home-calendar-btn${showCalendar ? ' home-calendar-btn--active' : ''}`}
              onClick={() => { setShowCalendar(!showCalendar); if (showCalendar) { setCalendarDate(null); } }}
              aria-label="달력"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
            </button>
            {currentProfile && (
              <button className="home-profile-btn" onClick={() => onNavigate('profileManage')}>
                <span className="home-profile-emoji">{currentProfile.emoji}</span>
                <span className="home-profile-name">{currentProfile.name}</span>
              </button>
            )}
          </div>
        </header>

        {/* 검색 바 */}
        {showSearch && (
          <div className="search-bar-wrap">
            <div className="search-bar">
              <svg className="search-bar-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                ref={searchInputRef}
                className="search-bar-input"
                type="text"
                placeholder={t('home_search_placeholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button className="search-bar-clear" onClick={() => setSearchQuery('')}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              )}
            </div>
            <button className="search-bar-cancel" onClick={closeSearch}>{t('home_cancel')}</button>
          </div>
        )}

        {/* 달력 위젯 */}
        {showCalendar && (
          <CalendarWidget
            records={records}
            onSelectDate={(date) => setCalendarDate(date)}
            onClose={() => { setShowCalendar(false); setCalendarDate(null); }}
          />
        )}

        {/* 카테고리 필터 + 정렬 */}
        {categoryOptions.length > 0 && (
          <div className="filter-sort-row">
            <div className="sort-wrap">
              <button
                className="sort-chip"
                onClick={() => setShowSortMenu(!showSortMenu)}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 6h18M6 12h12M9 18h6" />
                </svg>
              </button>
              {showSortMenu && (
                <div className="sort-menu">
                  <button className={sortOrder === 'newest' ? 'sort-menu-item--active' : ''} onClick={() => handleSortChange('newest')}>{t('home_sort_newest')}</button>
                  <button className={sortOrder === 'oldest' ? 'sort-menu-item--active' : ''} onClick={() => handleSortChange('oldest')}>{t('home_sort_oldest')}</button>
                </div>
              )}
            </div>
            <div className="category-filter-bar">
              <button
                className={`category-filter-btn${selectedCategory === null ? ' category-filter-btn--active' : ''}`}
                onClick={() => setSelectedCategory(null)}
              >
                {t('home_all')}
              </button>
              {categoryOptions.map((cat) => (
                <button
                  key={cat.name}
                  className={`category-filter-btn${selectedCategory === cat.name ? ' category-filter-btn--active' : ''}`}
                  onClick={() => setSelectedCategory(selectedCategory === cat.name ? null : cat.name)}
                >
                  {cat.emoji} {cat.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="timeline">
        {loading && (
          <div className="skeleton-list">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="skeleton-card">
                <div className="skeleton-emoji shimmer" />
                <div className="skeleton-body">
                  <div className="skeleton-line skeleton-line--title shimmer" />
                  <div className="skeleton-line skeleton-line--sub shimmer" />
                  <div className="skeleton-line skeleton-line--meta shimmer" />
                </div>
                <div className="skeleton-thumb shimmer" />
              </div>
            ))}
          </div>
        )}

        {!loading && fetchError && (
          <div className="timeline-error">
            <p>불러오기 실패: {fetchError}</p>
            <button onClick={fetchRecords}>다시 시도</button>
          </div>
        )}

        {isEmpty && !q && (
          <div className="timeline-empty">
            <p>{t('home_no_records')}</p>
            <p>{t('home_add_first')}</p>
          </div>
        )}

        {isEmpty && q && (
          <div className="timeline-empty">
            <p className="timeline-empty-icon">🔍</p>
            <p>"{searchQuery}"{t('home_no_search')}</p>
          </div>
        )}

        {/* 검색 결과 — 날짜 내림차순 flat 리스트 */}
        {q && filteredRecords.length > 0 && (
          <>
            <p className="search-result-count">{filteredRecords.length}{t('home_records_count')}</p>
            {filteredRecords.map((record) => (
              <RecordCard key={record.id} record={record} onNavigate={onNavigate} onLongPress={openDeleteSheet} />
            ))}
          </>
        )}

        {/* 일반 타임라인 — 연도별 그룹 */}
        {!q && (() => {
          let globalIdx = 0;
          let adIdx = 0;
          const totalRecords = yearGroups.reduce((sum, g) => sum + g.records.length, 0);
          return yearGroups.map(({ year, records: yearRecords }) => (
            <div key={year} className="year-group">
              <div className="year-header">
                <span className="year-label">{year}{t('year')}</span>
                <span className="year-count">{yearRecords.length}{t('count_unit')}</span>
              </div>
              {yearRecords.map((record) => {
                globalIdx++;
                const showAd = globalIdx % 5 === 0 && globalIdx < totalRecords;
                const currentAd = showAd ? adList[adIdx++ % adList.length] : null;
                return (
                  <div key={record.id}>
                    <RecordCard record={record} onNavigate={onNavigate} onLongPress={openDeleteSheet} />
                    {showAd && <AdCard ad={currentAd} />}
                  </div>
                );
              })}
            </div>
          ));
        })()}
      </div>

      {/* 롱프레스 삭제 시트 */}
      {deleteTarget && (
        <div
          className="home-sheet-backdrop"
          onClick={closeDeleteSheet}
          onPointerDown={() => { sheetArmedRef.current = true; }}
          onContextMenu={(e) => e.preventDefault()}
        >
          <div className="home-sheet" onClick={(e) => e.stopPropagation()}>
            <p className="home-sheet-title">
              {deleteTarget.category_emoji ?? '📌'} {deleteTarget.title}
            </p>
            <p className="home-sheet-desc">{t('confirm_delete')}</p>
            <div className="home-sheet-actions">
              <button className="home-sheet-cancel" onClick={closeDeleteSheet} disabled={deleting}>
                {t('home_cancel')}
              </button>
              <button className="home-sheet-delete" onClick={handleDeleteConfirm} disabled={deleting}>
                {deleting ? t('record_deleting') : t('record_delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* FAB */}
      <button className="fab" onClick={() => onNavigate('addRecord')} aria-label="기록 추가">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
          <line x1="12" y1="5" x2="12" y2="19" />
          <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
      </button>
    </div>
  );
}
